import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import * as z from "zod";

export const BaseFolderSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  table: z.string().meta({ privacy: privacy.nonPii }),
  name: z.string().meta({ privacy: privacy.nonPii }),
  position: z.number().meta({ privacy: privacy.nonPii }),
  updatedAt: z.coerce.date().nullish().meta({ privacy: privacy.nonPii }),
});

/**
 * Document-level schema representing how folders are stored in MongoDB.
 * Includes fields not exposed to clients (team).
 */
export const FolderDocumentSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  team: zExt.objectId().meta({ privacy: privacy.nonPii }),
  table: z.string().meta({ privacy: privacy.nonPii }),
  name: z.string().meta({ privacy: privacy.nonPii }),
  position: z.number().meta({ privacy: privacy.nonPii }),
});
export type FolderDocument = z.infer<typeof FolderDocumentSchema>;

export const folderSchemas = {
  "bulk-fulfillments": BaseFolderSchema,
  "fulfillment-groups": BaseFolderSchema,
  "pick-session": BaseFolderSchema,
  "pick-session-priority": BaseFolderSchema,
  "oms-priority": BaseFolderSchema,
  returns: BaseFolderSchema,
  claims: BaseFolderSchema,
  warranties: BaseFolderSchema,
  recycle: BaseFolderSchema,
  withdrawals: BaseFolderSchema,
  registrations: BaseFolderSchema,
  "purchase-orders": BaseFolderSchema,
  shipments: BaseFolderSchema,
  transfers: BaseFolderSchema,
  "inventory-levels": BaseFolderSchema,
  "catalog-products": BaseFolderSchema,
  "order-tracking-shipments": BaseFolderSchema,
  "order-tracking-orders": BaseFolderSchema,
  "work-orders": BaseFolderSchema,
  "work-order-priority": BaseFolderSchema,
  "replenishment-requests": BaseFolderSchema,
  "support-v2-conversations": BaseFolderSchema,
  "cycle-counts": BaseFolderSchema,
};

export enum FolderTypesNames {
  FULFILLMENT_GROUPS = "fulfillment-groups",
  PICK_SESSION = "pick-session",
  PICK_SESSION_PRIORITY = "pick-session-priority",
  OMS_PRIORITY = "oms-priority",
  RETURNS = "returns",
  CLAIMS = "claims",
  WARRANTIES = "warranties",
  RECYCLE = "recycle",
  WITHDRAWALS = "withdrawals",
  REGISTRATIONS = "registrations",
  PURCHASE_ORDERS = "purchase-orders",
  SHIPMENTS = "shipments",
  TRANSFERS = "transfers",
  INVENTORY_LEVELS = "inventory-levels",
  CATALOG_PRODUCTS = "catalog-products",
  ORDER_TRACKING_SHIPMENTS = "order-tracking-shipments",
  ORDER_TRACKING_ORDERS = "order-tracking-orders",
  WORK_ORDERS = "work-orders",
  WORK_ORDER_PRIORITY = "work-order-priority",
  REPLENISHMENT_REQUESTS = "replenishment-requests",
  SUPPORT_V2_CONVERSATIONS = "support-v2-conversations",
  CYCLE_COUNTS = "cycle-counts",
}

export type folderTypes = {
  [key in keyof typeof folderSchemas]: z.infer<(typeof folderSchemas)[key]>;
};

export const ClientFulfillmentGroupFolder = folderSchemas["fulfillment-groups"];
export type ClientFulfillmentGroupFolder = z.infer<
  typeof ClientFulfillmentGroupFolder
>;

export const ClientPickSessionFolder = folderSchemas["pick-session"];
export type ClientPickSessionFolder = z.infer<typeof ClientPickSessionFolder>;

export const ClientReturnFolder = folderSchemas.returns;
export type ClientReturnFolder = z.infer<typeof ClientReturnFolder>;

export const ClientClaimFolder = folderSchemas.claims;
export type ClientClaimFolder = z.infer<typeof ClientClaimFolder>;

export const ClientWarrantyFolder = folderSchemas.warranties;
export type ClientWarrantyFolder = z.infer<typeof ClientWarrantyFolder>;

export const ClientRecycleFolder = folderSchemas.recycle;
export type ClientRecycleFolder = z.infer<typeof ClientRecycleFolder>;

export const ClientWithdrawalFolder = folderSchemas.withdrawals;
export type ClientWithdrawalFolder = z.infer<typeof ClientWithdrawalFolder>;

export const ClientPurchaseOrderFolder = folderSchemas["purchase-orders"];
export type ClientPurchaseOrderFolder = z.infer<
  typeof ClientPurchaseOrderFolder
>;

export const ClientShipmentFolder = folderSchemas.shipments;
export type ClientShipmentFolder = z.infer<typeof ClientShipmentFolder>;

export const ClientInventoryLevelsFolder = folderSchemas["inventory-levels"];
export type ClientInventoryLevelsFolder = z.infer<
  typeof ClientInventoryLevelsFolder
>;

export const ClientCatalogProductsFolder = folderSchemas["catalog-products"];
export type ClientCatalogProductsFolder = z.infer<
  typeof ClientCatalogProductsFolder
>;

export const ClientOrderTrackingShipmentFolder =
  folderSchemas["order-tracking-shipments"];
export type ClientOrderTrackingShipmentFolder = z.infer<
  typeof ClientOrderTrackingShipmentFolder
>;

export const ClientOrderTrackingOrderFolder =
  folderSchemas["order-tracking-orders"];
export type ClientOrderTrackingOrderFolder = z.infer<
  typeof ClientOrderTrackingOrderFolder
>;

export const ClientWorkOrderFolder = folderSchemas["work-orders"];
export type ClientWorkOrderFolder = z.infer<typeof ClientWorkOrderFolder>;

export const ClientReplenishmentRequestFolder =
  folderSchemas["replenishment-requests"];
export type ClientReplenishmentRequestFolder = z.infer<
  typeof ClientReplenishmentRequestFolder
>;

export const ClientSupportV2ConversationFolder =
  folderSchemas["support-v2-conversations"];
export type ClientSupportV2ConversationFolder = z.infer<
  typeof ClientSupportV2ConversationFolder
>;

export const ClientCycleCountFolder = folderSchemas["cycle-counts"];
export type ClientCycleCountFolder = z.infer<typeof ClientCycleCountFolder>;
